// @ts-strict-ignore
import { getNormalizedHeaderValue, toCamelCase, toSnakeCase } from './util';

/** Base type for all requests */
type BaseRequestConfig = Omit<RequestInit, 'body' | 'credentials' | 'method'> & {
  meta?: {
    /**
     * Name of the request. Used in error messages and logs.
     */
    name?: string;
    /**
     * Return the whole response object instead of only the `data` field
     */
    returnFullResponse?: boolean;
    /**
     * Prevent error messages from being shown
     */
    silent?: boolean;
    /**
     * Prevent the request body case conversion to snake case
     */
    skipTransformRequestBody?: boolean;
    /**
     * Prevent the response body case conversion to camel case
     */
    skipTransformResponseBody?: boolean;
  };
};

/** Type for configs that require the full Response as the return value */
type FullResponseConfig = BaseRequestConfig & {
  meta: BaseRequestConfig['meta'] & { returnFullResponse: true };
};

/** Type for configs that only require the data of the Response as the return value  */
type DataResponseConfig = BaseRequestConfig & {
  meta?: BaseRequestConfig['meta'] & { returnFullResponse?: false };
};


/**
 * Creates a configurable API client
 */
export default function createAPIClient(args: {
  baseURL: string;
  showErrorMessage: (m: string) => void;
}) {
  const { baseURL, showErrorMessage } = args;

  const defaultHeaders: RequestInit['headers'] = {
    'content-type': 'application/json',
  };

  /** Configurable function to perform a network request */
  async function request<T>(
    url: string,
    method: string,
    data?: any,
    config: FullResponseConfig | DataResponseConfig = {},
  ): Promise<T | Response> {
    const fullUrl = new URL(url, baseURL);

    const headers = new Headers({
      ...defaultHeaders,
      ...config.headers,
    });

    let body = data;
    if (
      data
      && !config.meta?.skipTransformRequestBody
      && getNormalizedHeaderValue(headers, 'content-type').includes('application/json')) {
      body = toSnakeCase(data);
    }

    const response = await fetch(fullUrl, {
      ...config,
      body: JSON.stringify(body),
      credentials: 'include',
      headers,
      method,
    });

    if (!response.ok) {
      const errorCode = response.status;
      const showErrors = !config.meta?.silent;

      if (showErrors && errorCode === 403) {
        showErrorMessage('Error 403: You do not have permission to perform this action. Contact support for more information.');
      }

      if (showErrors && errorCode === 409) {
        showErrorMessage('Error 409: This action cannot be performed to due a conflict. Contact support for more information.');
      }

      if (showErrors && (errorCode >= 500 && errorCode < 600)) {
        const errorMessage = config.meta?.name
          ? `Error ${errorCode}: Internal server error while fetching ${config.meta?.name}. Contact support for more information.`
          : `Error ${errorCode}: Internal server error. Contact support for more information.`;
        showErrorMessage(errorMessage);
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }


    if (config.meta?.returnFullResponse) {
      return response;
    }

    let responseData = await response.json();

    if (
      responseData
      && getNormalizedHeaderValue(response.headers, 'content-type').includes('application/json')
      && !config.meta?.skipTransformResponseBody
    ) {
      responseData = toCamelCase(responseData);
    }

    return responseData;
  }


  // function overloads to support type-safe return values if the configuration requests a full response
  function get(url: string, config: FullResponseConfig): Promise<Response>;
  function get<T>(url: string, config?: DataResponseConfig): Promise<T>;
  function get<T>(
    url: string, config: FullResponseConfig | DataResponseConfig = {},
  ): Promise<T | Response> {
    return request<T>(url, 'GET', undefined, config);
  }

  function post(url: string, data: any, config: FullResponseConfig): Promise<Response>;
  function post<T>(url: string, data: any, config?: DataResponseConfig): Promise<T>;
  function post<T>(
    url: string, data: any, config: FullResponseConfig | DataResponseConfig = {},
  ): Promise<T | Response> {
    return request<T>(url, 'POST', data, config);
  }

  function put(url: string, data: any, config: FullResponseConfig): Promise<Response>;
  function put<T>(url: string, data: any, config?: DataResponseConfig): Promise<T>;
  function put<T>(
    url: string, data: any, config: FullResponseConfig | DataResponseConfig = {},
  ): Promise<T | Response> {
    return request<T>(url, 'PUT', data, config);
  }

  function patch(url: string, data: any, config: FullResponseConfig): Promise<Response>;
  function patch<T>(url: string, data: any, config?: DataResponseConfig): Promise<T>;
  function patch<T>(
    url: string, data: any, config: FullResponseConfig | DataResponseConfig = {},
  ): Promise<T | Response> {
    return request<T>(url, 'PATCH', data, config);
  }

  // `delete` is a reserved JS word to it cannot be used as function name
  function del(url: string, config: FullResponseConfig): Promise<Response>;
  function del<T>(url: string, config?: DataResponseConfig): Promise<T>;
  function del<T>(
    url: string, config: FullResponseConfig | DataResponseConfig = {},
  ): Promise<T | Response> {
    return request<T>(url, 'DELETE', undefined, config);
  }


  // Interface of the api client
  return {
    get,
    post,
    put,
    patch,
    delete: del,
  };
}

export type APIClient = ReturnType<typeof createAPIClient>;
